import { Flag } from '../ui/country-select.jsx';
import { countryName } from '../../lib/countries.js';
import { formatUtcInTimezone } from '../../lib/time.js';
import { UnitChannelSection } from './unit-channel-section.jsx';
import { PersonChannelsSection } from './person-channels-section.jsx';

/**
 * Right-pane detail card. Shows either a unit (with breadcrumbs and direct
 * personnel) or a person (with full attributes and unit context).
 */
export function OssDetail({
  selection,
  units,           // flat lookup: id -> unit
  persons,         // flat lookup: id -> person (for person selection)
  nodes,           // flat lookup: id -> node
  ancestry,        // [unit...] root → leaf for the selected unit
  inheritedHome, // resolved anchor for the selected unit (if any)
  viewerTimezone,
  isAdmin,
  onAddSubUnit,
  onAddPerson,
  onEditUnit,
  onEditPerson,
  onDeleteUnit,
  onDeletePerson,
  onMoveUnit,
  onMovePerson,
}) {
  if (!selection) {
    return (
      <div className="card flex h-full flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-canvas text-mist">
          <i className="fa-solid fa-hand-pointer text-lg" aria-hidden="true" />
        </div>
        <p className="text-sm text-mist">Select a unit or person to see details.</p>
      </div>
    );
  }

  if (selection.kind === 'unit') {
    const unit = units[selection.id];
    if (!unit) return null;
    return (
      <UnitDetail
        unit={unit}
        ancestry={ancestry}
        nodes={nodes}
        inheritedHome={inheritedHome}
        viewerTimezone={viewerTimezone}
        isAdmin={isAdmin}
        onAddSubUnit={onAddSubUnit}
        onAddPerson={onAddPerson}
        onEditUnit={onEditUnit}
        onDeleteUnit={onDeleteUnit}
        onMoveUnit={onMoveUnit}
        onEditPerson={onEditPerson}
        onDeletePerson={onDeletePerson}
        onMovePerson={onMovePerson}
      />
    );
  }

  const person = persons[selection.id];
  if (!person) return null;
  const unit = units[person.unitId];
  return (
    <PersonDetail
      person={person}
      unit={unit}
      viewerTimezone={viewerTimezone}
      isAdmin={isAdmin}
      onEdit={() => onEditPerson(person, unit)}
      onMove={() => onMovePerson(person)}
      onDelete={() => onDeletePerson(person)}
    />
  );
}

function UnitDetail({
  unit, ancestry, nodes, inheritedHome, viewerTimezone, isAdmin,
  onAddSubUnit, onAddPerson, onEditUnit, onDeleteUnit, onMoveUnit,
  onEditPerson, onDeletePerson, onMovePerson,
}) {
  const directPersons = unit.persons || [];
  const childCount = (unit.children || []).length;
  const homeNode = unit.homeNodeId ? nodes[unit.homeNodeId] : null;

  return (
    <div className="card overflow-hidden">
      <header className="flex items-start justify-between gap-3 border-b border-line bg-gradient-to-br from-accent-soft via-white to-canvas px-6 py-5">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-accent text-white shadow-card">
            <i className="fa-solid fa-sitemap text-xl" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            {ancestry && ancestry.length > 1 && (
              <Breadcrumbs items={ancestry.slice(0, -1)} />
            )}
            <h2 className="mt-0.5 truncate text-xl font-semibold tracking-tight text-ink">{unit.name}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-mist">
              {unit.shortName && (
                <span className="rounded-full border border-line bg-white px-2 py-0.5 font-mono uppercase tracking-wider">
                  {unit.shortName}
                </span>
              )}
              {unit.countryCode && (
                <span className="inline-flex items-center gap-1.5">
                  <Flag code={unit.countryCode} size={14} />
                  {countryName(unit.countryCode)}
                </span>
              )}
              {childCount > 0 && (
                <span><i className="fa-solid fa-folder-tree mr-1" aria-hidden="true" />{childCount} sub-units</span>
              )}
              <span><i className="fa-solid fa-user mr-1" aria-hidden="true" />{directPersons.length} direct</span>
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex shrink-0 items-center gap-1">
            <button type="button" onClick={() => onAddSubUnit(unit)} className="btn-secondary text-xs" title="Add sub-unit">
              <i className="fa-solid fa-folder-plus" aria-hidden="true" /> Sub-unit
            </button>
            <button type="button" onClick={() => onAddPerson(unit)} className="btn-secondary text-xs" title="Add person">
              <i className="fa-solid fa-user-plus" aria-hidden="true" /> Person
            </button>
            <button type="button" onClick={() => onEditUnit(unit)} className="btn-ghost text-xs" title="Edit">
              <i className="fa-solid fa-pen" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => onMoveUnit(unit)} className="btn-ghost text-xs" title="Move">
              <i className="fa-solid fa-arrows-up-down-left-right" aria-hidden="true" />
            </button>
            <button type="button" onClick={() => onDeleteUnit(unit)} className="btn-ghost text-xs text-rose-600 hover:bg-rose-soft" title="Delete">
              <i className="fa-solid fa-trash" aria-hidden="true" />
            </button>
          </div>
        )}
      </header>

      {/* Metadata */}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 px-6 py-5 sm:grid-cols-2">
        <Field label="Home node">
          {homeNode ? (
            <span className="inline-flex items-center gap-2">
              <i className="fa-solid fa-server text-accent" aria-hidden="true" />
              <span className="font-mono">{homeNode.name}</span>
              <span className="text-[11px] text-mist">{homeNode.tier}</span>
            </span>
          ) : inheritedHome ? (
            <span className="inline-flex items-center gap-2 text-mist">
              <i className="fa-solid fa-arrow-up text-[11px]" aria-hidden="true" />
              inherited:
              <span className="font-mono text-ink">{inheritedHome.name}</span>
            </span>
          ) : (
            <span className="text-mist">—</span>
          )}
        </Field>

        <Field label="Coordinates" mono>
          {unit.latitude != null && unit.longitude != null
            ? `${unit.latitude.toFixed(4)}°, ${unit.longitude.toFixed(4)}°`
            : '—'}
        </Field>

        <Field label="Created" full>
          {formatUtcInTimezone(unit.createdAt, viewerTimezone)}
        </Field>

        {unit.description && (
          <Field label="Description" full>
            <span className="whitespace-pre-wrap text-sm text-ink">{unit.description}</span>
          </Field>
        )}
      </dl>

      {/* Direct personnel */}
      <div className="border-t border-line bg-canvas/40 px-6 py-3 text-[11px] font-semibold uppercase tracking-wider text-mist">
        Direct personnel ({directPersons.length})
      </div>
      {directPersons.length === 0 ? (
        <div className="px-6 py-6 text-center text-sm text-mist">
          No personnel directly under this unit.
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {directPersons.map((p) => (
            <li key={p.id} className="flex items-center gap-3 px-6 py-3">
              <div className={'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ' + (p.hasDevice ? 'bg-accent-soft text-accent' : 'bg-canvas text-mist')}>
                {(p.displayName || '?').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  {p.rank && <span className="font-mono text-[10px] uppercase text-mist">{p.rank}</span>}
                  <span className="truncate font-semibold text-ink">{p.displayName}</span>
                  {p.callsign && (
                    <span className="rounded border border-line bg-white px-1.5 py-0.5 font-mono text-[10px] text-mist">{p.callsign}</span>
                  )}
                  {p.hasDevice && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
                      <i className="fa-solid fa-mobile-screen text-[9px]" aria-hidden="true" />
                      device
                    </span>
                  )}
                </div>
                {p.position && (
                  <div className="text-[11px] text-mist">{p.position}</div>
                )}
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => onEditPerson(p, unit)} className="btn-ghost text-xs" title="Edit">
                    <i className="fa-solid fa-pen" aria-hidden="true" />
                  </button>
                  <button type="button" onClick={() => onMovePerson(p)} className="btn-ghost text-xs" title="Move">
                    <i className="fa-solid fa-arrows-up-down-left-right" aria-hidden="true" />
                  </button>
                  <button type="button" onClick={() => onDeletePerson(p)} className="btn-ghost text-xs text-rose-600 hover:bg-rose-soft" title="Delete">
                    <i className="fa-solid fa-trash" aria-hidden="true" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <UnitChannelSection
        unit={unit}
        viewerTimezone={viewerTimezone}
        isAdmin={isAdmin}
      />
    </div>
  );
}

function PersonDetail({ person, unit, viewerTimezone, isAdmin, onEdit, onMove, onDelete }) {
  return (
    <div className="card overflow-hidden">
      <header className="flex items-start justify-between gap-3 border-b border-line bg-gradient-to-br from-canvas via-white to-accent-soft px-6 py-5">
        <div className="flex min-w-0 items-start gap-3">
          <div className={'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-base font-bold ' + (person.hasDevice ? 'bg-accent text-white' : 'bg-canvas text-mist')}>
            {(person.displayName || '?').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            {unit && (
              <Breadcrumbs items={[{ id: unit.id, name: unit.name }]} />
            )}
            <h2 className="mt-0.5 truncate text-xl font-semibold tracking-tight text-ink">{person.displayName}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              {person.rank && (
                <span className="rounded-full border border-line bg-white px-2 py-0.5 font-mono uppercase tracking-wider text-mist">
                  {person.rank}
                </span>
              )}
              {person.callsign && (
                <span className="rounded-full border border-line bg-white px-2 py-0.5 font-mono text-mist">
                  {person.callsign}
                </span>
              )}
              {person.hasDevice ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
                  <i className="fa-solid fa-mobile-screen text-[9px]" aria-hidden="true" />
                  Tactical device
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full border border-line bg-canvas px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-mist">
                  No device
                </span>
              )}
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex shrink-0 items-center gap-1">
            <button type="button" onClick={onEdit} className="btn-secondary text-xs" title="Edit">
              <i className="fa-solid fa-pen" aria-hidden="true" /> Edit
            </button>
            <button type="button" onClick={onMove} className="btn-ghost text-xs" title="Move">
              <i className="fa-solid fa-arrows-up-down-left-right" aria-hidden="true" />
            </button>
            <button type="button" onClick={onDelete} className="btn-ghost text-xs text-rose-600 hover:bg-rose-soft" title="Delete">
              <i className="fa-solid fa-trash" aria-hidden="true" />
            </button>
          </div>
        )}
      </header>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 px-6 py-5 sm:grid-cols-2">
        <Field label="Position">{person.position || '—'}</Field>
        <Field label="Unit">
          {unit ? (
            <span className="font-mono">{unit.name}</span>
          ) : (
            <span className="text-mist">—</span>
          )}
        </Field>
        <Field label="Created">{formatUtcInTimezone(person.createdAt, viewerTimezone)}</Field>
        <Field label="Updated">{formatUtcInTimezone(person.updatedAt, viewerTimezone)}</Field>
      </dl>

      <PersonChannelsSection personId={person.id} />
    </div>
  );
}

function Breadcrumbs({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 text-[11px] font-mono text-mist">
      {items.map((u, i) => (
        <span key={u.id} className="inline-flex items-center gap-1">
          {i > 0 && <i className="fa-solid fa-chevron-right text-[8px]" aria-hidden="true" />}
          <span>{u.name}</span>
        </span>
      ))}
    </div>
  );
}

function Field({ label, children, mono, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</dt>
      <dd className={`mt-0.5 text-sm ${mono ? 'font-mono text-ink' : 'text-ink'}`}>{children}</dd>
    </div>
  );
}
